import { Globe, Smartphone, Server, Palette } from 'lucide-react';

const services = [
  {
    icon: Globe,
    title: 'Web Development',
    description: 'Custom web applications built with modern frameworks like React, Next.js, and Vue. Fast, responsive, and SEO-optimized.',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    icon: Smartphone,
    title: 'Mobile App Development',
    description: 'Native and cross-platform mobile apps for iOS and Android using React Native and Flutter.',
    gradient: 'from-indigo-500 to-purple-500',
  },
  {
    icon: Server,
    title: 'Backend & API Development',
    description: 'Scalable backend systems, RESTful APIs, and microservices architecture using Node.js, Python, and Go.',
    gradient: 'from-emerald-500 to-teal-500',
  },
  {
    icon: Palette,
    title: 'UI/UX Design',
    description: 'User-centered design that combines aesthetics with functionality. Prototyping, wireframing, and design systems.',
    gradient: 'from-pink-500 to-rose-500',
  },
];

const Services = () => {
  return (
    <section 
      id="services" 
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="services-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-slate-950">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full border border-slate-800 bg-slate-900/50 text-sm text-blue-400 mb-4">
            Our Services
          </span>
          <h2 
            className="font-space text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4"
            data-testid="services-title"
          >
            What We Build
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-base md:text-lg">
            End-to-end software solutions tailored to your business needs
          </p>
        </div>

        {/* Services Grid */}
        <div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          data-testid="services-grid"
        >
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-2xl bg-slate-900/40 border border-slate-800 p-8 transition-all duration-500 hover:border-blue-500/30 hover:bg-slate-900/60"
              data-testid={`service-card-${index}`}
            >
              {/* Hover effect */}
              <div className="absolute inset-0 -z-10 translate-x-[-100%] bg-gradient-to-r from-transparent via-blue-500/5 to-transparent transition-transform duration-700 group-hover:translate-x-[100%]" />
              
              {/* Icon */}
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} mb-6`}>
                <service.icon className="w-7 h-7 text-white" />
              </div>

              {/* Content */}
              <h3 className="font-space text-xl md:text-2xl font-semibold text-white mb-3 group-hover:text-blue-400 transition-colors">
                {service.title}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {service.description}
              </p>

              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
